import type {
	CreateProductPriceDto,
	CreateProductRequestDto,
	ProductItemDto,
	ProductPriceDto,
	UpdateProductRequestDto,
} from '@/types/api/product';
import { toWritableApiProductType } from '@/utils/productUtils';

/**
 * 문서 불일치 요약 (저장소 기준)
 *
 * - `docs/api/i18n-api-changes.json`「프로덕트 수정」
 *   → `items[].lectureList[].translations` 안에 `videoUrl` 포함 (중첩 형태)
 *
 * - `docs/api/openapi.json` → `components.schemas.CreateProductItemDto`
 *   → **`lectureList` 없음** — `lectureTranslations: [{ language, title, description? }]`
 *   → 동영상 URL은 **`lectureVideoUrl`** 항목 필드 (번역 객체에 `videoUrl` 없음)
 *
 * 기본값은 **OpenAPI**에 맞춤. i18n 시트 그대로 보내려면
 * `REACT_APP_PRODUCT_PUT_I18N_NESTED_LECTURE=true`
 *
 * 구 백엔드가 루트 `title`/`description`을 병행 요구하면
 * `REACT_APP_PRODUCT_PUT_LEGACY_OPENAPI_FIELDS=true`
 */
function mergeLegacyOpenApiUpdateProductFields(base: UpdateProductRequestDto): UpdateProductRequestDto {
	const t0 = base.translations[0];
	return {
		...base,
		title: (t0?.title ?? '').trim(),
		description: (t0 && 'description' in t0 ? String(t0.description ?? '') : '').trim(),
	};
}

function pickFirstLectureVideoUrl(translations: { videoUrl?: string }[]): string {
	for (const tr of translations) {
		const v = typeof tr.videoUrl === 'string' ? tr.videoUrl.trim() : '';
		if (v) return v;
	}
	return '';
}

/** 프로덕트 번역 배열에서 요청 항목 `language` 누락 시 기본값 (백엔드 DTO 필수 필드) */
function resolveTranslationLanguage(
	lang: string | undefined,
	fallback: string
): string {
	const t = typeof lang === 'string' ? lang.trim() : '';
	return t || fallback;
}

/**
 * OpenAPI `ProductTranslationDto[]` 필수: 최소 1개, 각 항목에 `language`·`title` 필수.
 * - 빈 배열이면 PUT 검증/서버에서 실패할 수 있음 → 최소 1행 보장.
 * - 구 스펙 루트 `title`/`description`만 오는 경우 `legacy`로 1행 구성.
 * - `language`가 비면 JSON 직렬화 시 키가 빠질 수 있어 항상 문자열로 채움.
 */
function normalizeRequestTranslations(
	list: UpdateProductRequestDto['translations'] | CreateProductRequestDto['translations'] | undefined,
	legacy?: { title?: string; description?: string }
): { language: string; title: string; description?: string }[] {
	let source = [...(list ?? [])];
	if (source.length === 0 && legacy) {
		const t0 = (legacy.title ?? '').trim();
		const d0 = (legacy.description ?? '').trim();
		if (t0 || d0) {
			source = [{ language: 'ko', title: t0 || ' ', ...(d0 ? { description: d0 } : {}) }];
		}
	}
	if (source.length === 0) {
		source = [{ language: 'ko', title: ' ' }];
	}
	const rawFirst =
		typeof source[0]?.language === 'string' ? source[0].language.trim() : '';
	const fb = rawFirst || 'ko';
	return source.map((t) => {
		const language = resolveTranslationLanguage(t.language, fb);
		/** 빈 title 은 DTO @IsNotEmpty() 등에서 예외 유발 가능 — 최소 1글자 보장 */
		const title = (t.title ?? '').trim() || ' ';
		const description = (t.description ?? '').trim();
		return {
			language,
			title,
			...(description ? { description } : {}),
		};
	});
}

/**
 * OpenAPI `CreateProductPriceDto` / `ProductPriceDto` — currency·purchaseType enum 정규화.
 * `NONE`·레거시 PER_LICENSE/PER_ITEM 은 문서·서버 스펙에 맞게 보존·매핑.
 */
function sanitizeNestedProductPriceRow<T extends CreateProductPriceDto | ProductPriceDto>(row: T): T {
	const purchaseType: ProductPriceDto['purchaseType'] =
		row.purchaseType === 'NONE'
			? 'NONE'
			: row.purchaseType === 'PER_DEVICE'
				? 'PER_DEVICE'
				: row.purchaseType === 'PER_USER'
					? 'PER_USER'
					: row.purchaseType === 'PER_ITEM'
						? 'PER_DEVICE'
						: row.purchaseType === 'PER_LICENSE'
							? 'PER_USER'
							: 'PER_USER';
	const currency: ProductPriceDto['currency'] = row.currency === 'USD' ? 'USD' : 'KRW';
	const translations = row.translations.map((tr) => ({
		language: resolveTranslationLanguage(tr.language, 'ko'),
		title: (tr.title ?? '').trim() || ' ',
	}));
	return { ...row, purchaseType, currency, translations } as T;
}

/**
 * POST/PUT 공통 — `CreateProductItemDto` / OpenAPI: 강의는 `lectureTranslations[].language` 필수,
 * 동영상 URL은 항목 `lectureVideoUrl`. `lectureList`·평면 필드(`lectureTitle` 등)를 OpenAPI 형태로 맞춤.
 */
function normalizeProductItemsForOpenApi(items: ProductItemDto[], fallbackLang: string): ProductItemDto[] {
	const i18nNested =
		String(process.env.REACT_APP_PRODUCT_PUT_I18N_NESTED_LECTURE ?? '').toLowerCase() === 'true';
	const fb = resolveTranslationLanguage(fallbackLang, 'ko');

	return items.map((item) => {
		if (item.scenarioId != null && item.scenarioId > 0) {
			return { order: item.order, scenarioId: item.scenarioId };
		}
		if (item.lectureList?.length) {
			if (i18nNested) {
				return {
					order: item.order,
					lectureList: item.lectureList.map((lec) => ({
						translations: lec.translations.map((tr) => {
							const desc = (tr.description ?? '').toString().trim();
							return {
								language: resolveTranslationLanguage(tr.language, fb),
								title: (tr.title ?? '').trim(),
								videoUrl: (tr.videoUrl ?? '').trim(),
								...(desc ? { description: desc } : {}),
							};
						}),
					})),
				};
			}
			const lec = item.lectureList[0];
			const lectureTranslations = lec.translations.map((tr) => {
				const desc = (tr.description ?? '').toString().trim();
				return {
					language: resolveTranslationLanguage(tr.language, fb),
					title: (tr.title ?? '').trim() || ' ',
					...(desc ? { description: desc } : {}),
				};
			});
			const lectureVideoUrl = pickFirstLectureVideoUrl(lec.translations);
			const creator =
				typeof item.lectureCreatorName === 'string' && item.lectureCreatorName.trim()
					? item.lectureCreatorName.trim()
					: undefined;
			return {
				order: item.order,
				lectureTranslations,
				...(lectureVideoUrl ? { lectureVideoUrl } : {}),
				...(creator ? { lectureCreatorName: creator } : {}),
			};
		}

		const urlFromItem =
			typeof item.lectureVideoUrl === 'string' ? item.lectureVideoUrl.trim() : '';
		if (item.lectureTranslations?.length || urlFromItem) {
			const trs = (item.lectureTranslations ?? []).map((tr) => {
				const desc = (tr.description ?? '').toString().trim();
				return {
					language: resolveTranslationLanguage(tr.language, fb),
					title: (tr.title ?? '').trim() || ' ',
					...(desc ? { description: desc } : {}),
				};
			});
			if (trs.length === 0 && urlFromItem) {
				const t0 = (item.lectureTitle ?? '').toString().trim();
				trs.push({
					language: fb,
					title: t0 || ' ',
				});
			}
			const creator =
				typeof item.lectureCreatorName === 'string' && item.lectureCreatorName.trim()
					? item.lectureCreatorName.trim()
					: undefined;
			if (trs.length === 0 && !urlFromItem) {
				return { order: item.order };
			}
			return {
				order: item.order,
				lectureTranslations: trs,
				...(urlFromItem ? { lectureVideoUrl: urlFromItem } : {}),
				...(creator ? { lectureCreatorName: creator } : {}),
			};
		}

		const flatTitle = (item.lectureTitle ?? '').toString().trim();
		const flatUrl = (item.lectureVideoUrl ?? '').toString().trim();
		const flatDesc = (item.lectureDescription ?? '').toString().trim();
		const flatCreator =
			typeof item.lectureCreatorName === 'string' && item.lectureCreatorName.trim()
				? item.lectureCreatorName.trim()
				: undefined;
		if (flatTitle || flatUrl || flatDesc || flatCreator) {
			return {
				order: item.order,
				lectureTranslations: [
					{
						language: fb,
						title: flatTitle || ' ',
						...(flatDesc ? { description: flatDesc } : {}),
					},
				],
				...(flatUrl ? { lectureVideoUrl: flatUrl } : {}),
				...(flatCreator ? { lectureCreatorName: flatCreator } : {}),
			};
		}

		return { order: item.order };
	});
}

export function normalizeCreateProductPayload(data: CreateProductRequestDto): CreateProductRequestDto {
	const translations = normalizeRequestTranslations(data.translations, undefined);
	const fb = translations[0]?.language ?? 'ko';
	const items = normalizeProductItemsForOpenApi(data.items, fb);
	const prices = (data.prices ?? []).map(sanitizeNestedProductPriceRow);
	return {
		...data,
		type: toWritableApiProductType(data.type),
		translations,
		items,
		prices,
	};
}

export function normalizeUpdateProductPayload(data: UpdateProductRequestDto): UpdateProductRequestDto {
	const translations = normalizeRequestTranslations(data.translations, {
		title: data.title,
		description: data.description,
	});
	const fb = translations[0]?.language ?? 'ko';
	const items = normalizeProductItemsForOpenApi(data.items, fb);

	let result: UpdateProductRequestDto = {
		type: toWritableApiProductType(data.type),
		translations,
		items,
		...(data.createPriceList?.length
			? { createPriceList: data.createPriceList.map(sanitizeNestedProductPriceRow) }
			: {}),
		...(data.deletePriceList?.length ? { deletePriceList: data.deletePriceList } : {}),
		...(data.activeChangePriceList?.length ? { activeChangePriceList: data.activeChangePriceList } : {}),
		...(data.partnerOrganizationId !== undefined
			? { partnerOrganizationId: data.partnerOrganizationId }
			: {}),
		isActive: data.isActive,
	};

	const legacy =
		String(process.env.REACT_APP_PRODUCT_PUT_LEGACY_OPENAPI_FIELDS ?? '').toLowerCase() === 'true';
	if (legacy) {
		result = mergeLegacyOpenApiUpdateProductFields(result);
	}

	return result;
}
